import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { useUserAuthStore } from '../store/userAuthStore'
import { Loader2 } from 'lucide-react'

export default function LoginPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { login: adminLogin, verifyTwoFactor, twoFactorRequired, tempToken } = useAuthStore()
  const {
    login: userLogin,
    register: userRegister,
    loading: userLoading,
    error: userStoreError,
  } = useUserAuthStore()

  const searchMode = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('mode') === 'user' ? 'user' : 'admin'
  }, [location.search])

  const [authMode, setAuthMode] = useState<'admin' | 'user'>(searchMode)
  const [userFormMode, setUserFormMode] = useState<'login' | 'register'>('login')

  useEffect(() => {
    setAuthMode(searchMode)
  }, [searchMode])

  const [adminEmail, setAdminEmail] = useState('')
  const [adminPassword, setAdminPassword] = useState('')
  const [twoFactorCode, setTwoFactorCode] = useState('')
  const [adminLoading, setAdminLoading] = useState(false)
  const [adminError, setAdminError] = useState('')

  const [userEmail, setUserEmail] = useState('')
  const [userPassword, setUserPassword] = useState('')
  const [userDisplayName, setUserDisplayName] = useState('')
  const [userUsername, setUserUsername] = useState('')
  const [userError, setUserError] = useState('')

  const handleModeChange = (mode: 'admin' | 'user') => {
    setAuthMode(mode)
    setUserFormMode('login')
    setAdminError('')
    setUserError('')
    if (typeof window !== 'undefined') {
      navigate(`/login?mode=${mode}`, { replace: true })
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setAdminError('')
    setAdminLoading(true)

    try {
      const result = await adminLogin(adminEmail, adminPassword)
      if (!result.requiresTwoFactor) {
        navigate('/')
      }
    } catch (err: any) {
      // Show user-friendly error message
      const errorMessage = err.message || err.response?.data?.message || err.response?.data?.error || 'Login failed'
      setAdminError(errorMessage)
      console.error('Login failed:', err)
    } finally {
      setAdminLoading(false)
    }
  }

  const handleVerifyTwoFactor = async (e: React.FormEvent) => {
    e.preventDefault()
    setAdminError('')
    setAdminLoading(true)

    try {
      await verifyTwoFactor(twoFactorCode, tempToken!)
      navigate('/')
    } catch (err: any) {
      setAdminError(err.response?.data?.message || 'Invalid code')
    } finally {
      setAdminLoading(false)
    }
  }

  const handleUserLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setUserError('')

    try {
      await userLogin(userEmail, userPassword)
      navigate('/user/pin')
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to log in'
      setUserError(errorMessage)
    }
  }

  const handleUserRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setUserError('')

    if (!userDisplayName.trim()) {
      setUserError('Display name is required')
      return
    }

    try {
      await userRegister({
        email: userEmail,
        password: userPassword,
        display_name: userDisplayName.trim(),
        username: userUsername.trim() || undefined,
      })
      navigate('/user/pin')
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to register'
      setUserError(errorMessage)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat px-4" style={{ backgroundImage: 'url(/bg.png)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img src="/icon.svg" alt="Waybook" className="h-16 w-16" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            {authMode === 'admin' ? 'Waybook Admin' : 'Waybook Tester'}
          </h1>
          <p className="text-gray-600 mt-2">
            {authMode === 'admin'
              ? 'Sign in to access the admin panel'
              : 'Log in with a Waybook account to explore the public API'}
          </p>
        </div>

        <div className="card">
          <div className="grid grid-cols-2 gap-2 mb-6">
            <button
              type="button"
              onClick={() => handleModeChange('admin')}
              className={`h-10 rounded-lg border transition ${authMode === 'admin' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white border-gray-200 text-gray-600'}`}
            >
              Admin
            </button>
            <button
              type="button"
              onClick={() => handleModeChange('user')}
              className={`h-10 rounded-lg border transition ${authMode === 'user' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white border-gray-200 text-gray-600'}`}
            >
              User
            </button>
          </div>

          {authMode === 'admin' ? (
            !twoFactorRequired ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label htmlFor="admin-email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    id="admin-email"
                    type="email"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    className="input w-full"
                    placeholder="Enter your email"
                    required
                    autoFocus={authMode === 'admin'}
                  />
                </div>

                <div>
                  <label htmlFor="admin-password" className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <input
                    id="admin-password"
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    className="input w-full"
                    placeholder="Enter your password"
                    required
                  />
                </div>

                {adminError && (
                  <div className="p-3 rounded-lg bg-red-50 text-red-800 text-sm">
                    {adminError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={adminLoading}
                  className="btn-primary w-full h-11"
                >
                  {adminLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    'Sign in'
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyTwoFactor} className="space-y-4">
                <div className="text-center mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Two-Factor Authentication</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Enter the 6-digit code from your authenticator app
                  </p>
                </div>

                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                    Authentication Code
                  </label>
                  <input
                    id="code"
                    type="text"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="input w-full text-center text-2xl tracking-widest"
                    placeholder="000000"
                    maxLength={6}
                    required
                    autoFocus
                  />
                </div>

                {adminError && (
                  <div className="p-3 rounded-lg bg-red-50 text-red-800 text-sm">
                    {adminError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={adminLoading || twoFactorCode.length !== 6}
                  className="btn-primary w-full h-11"
                >
                  {adminLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    'Verify'
                  )}
                </button>
              </form>
            )
          ) : (
            <div>
              <div className="flex gap-2 mb-6 bg-gray-50 p-1 rounded-lg">
                <button
                  type="button"
                  onClick={() => {
                    setUserFormMode('login')
                    setUserError('')
                  }}
                  className={`flex-1 h-9 rounded-md text-sm font-medium transition ${
                    userFormMode === 'login'
                      ? 'bg-white shadow border border-gray-200 text-gray-900'
                      : 'text-gray-600'
                  }`}
                >
                  Sign in
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setUserFormMode('register')
                    setUserError('')
                  }}
                  className={`flex-1 h-9 rounded-md text-sm font-medium transition ${
                    userFormMode === 'register'
                      ? 'bg-white shadow border border-gray-200 text-gray-900'
                      : 'text-gray-600'
                  }`}
                >
                  Register
                </button>
              </div>

              {userFormMode === 'login' ? (
                <form onSubmit={handleUserLogin} className="space-y-4">
                  <div>
                    <label htmlFor="user-email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      id="user-email"
                      type="email"
                      value={userEmail}
                      onChange={(e) => setUserEmail(e.target.value)}
                      className="input w-full"
                      placeholder="Enter your email"
                      required
                      autoFocus={authMode === 'user'}
                    />
                  </div>

                  <div>
                    <label htmlFor="user-password" className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <input
                      id="user-password"
                      type="password"
                      value={userPassword}
                      onChange={(e) => setUserPassword(e.target.value)}
                      className="input w-full"
                      placeholder="Enter your password"
                      required
                      minLength={8}
                    />
                  </div>

                  {(userError || userStoreError) && (
                    <div className="p-3 rounded-lg bg-red-50 text-red-800 text-sm">
                      {userError || userStoreError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={userLoading}
                    className="btn-primary w-full h-11"
                  >
                    {userLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      'Sign in as user'
                    )}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleUserRegister} className="space-y-4">
                  <div>
                    <label htmlFor="register-display-name" className="block text-sm font-medium text-gray-700 mb-1">
                      Display name
                    </label>
                    <input
                      id="register-display-name"
                      type="text"
                      value={userDisplayName}
                      onChange={(e) => setUserDisplayName(e.target.value)}
                      className="input w-full"
                      placeholder="Name other users will see"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="register-username" className="block text-sm font-medium text-gray-700 mb-1">
                      Username (optional)
                    </label>
                    <input
                      id="register-username"
                      type="text"
                      value={userUsername}
                      onChange={(e) => setUserUsername(e.target.value)}
                      className="input w-full"
                      placeholder="Choose a username"
                    />
                  </div>

                  <div>
                    <label htmlFor="register-email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      id="register-email"
                      type="email"
                      value={userEmail}
                      onChange={(e) => setUserEmail(e.target.value)}
                      className="input w-full"
                      placeholder="Enter your email"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="register-password" className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <input
                      id="register-password"
                      type="password"
                      value={userPassword}
                      onChange={(e) => setUserPassword(e.target.value)}
                      className="input w-full"
                      placeholder="Create a password (min 8 characters)"
                      minLength={8}
                      required
                    />
                  </div>

                  {(userError || userStoreError) && (
                    <div className="p-3 rounded-lg bg-red-50 text-red-800 text-sm">
                      {userError || userStoreError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={userLoading}
                    className="btn-primary w-full h-11"
                  >
                    {userLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      'Create account'
                    )}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
